/**
 * Eine Zeile unter jedem KIAN-Ergebnis: welches Modell, wie viele Tokens, was es gekostet hat.
 *
 * Die Zahlen kommen vom Server (`kianUsage` an der Antwort bzw. gespeichert am Ergebnis),
 * gerechnet über dieselbe Preisquelle wie die LLM-Statistik — hier wird nichts geschätzt.
 */
import type { ReactNode } from 'react';
import { Text, Tooltip, type TextProps } from '@mantine/core';
import type { KianUsage } from '../lib/api/types/core';

/** „openai/gpt-5.4-mini" → „gpt-5.4-mini": der Anbieter-Präfix sagt am Ergebnis niemandem etwas. */
export function shortModelName(model: string): string {
  return model.slice(model.lastIndexOf('/') + 1);
}

const nf = new Intl.NumberFormat('de-DE');

/** Beträge unter einem Cent in Cent, sonst stünde bei fast jedem Aufruf „0,00 €". */
export function formatKianCost(eur: number): string {
  if (eur < 0.01) return `${(eur * 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} ct`;
  return `${eur.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export function formatKianUsage(usage: KianUsage): string {
  const parts = [usage.models.map(shortModelName).join(' + ') || 'unbekanntes Modell'];
  parts.push(`${nf.format(usage.tokensIn + usage.tokensOut)} Tokens`);
  if (usage.costEur != null) parts.push(`${usage.costEstimated ? '~' : ''}${formatKianCost(usage.costEur)}`);
  return parts.join(' · ');
}

/** `llmUsage` aus einem JSON-Feld (z. B. `contextMeta`) — alles Unerwartete wird zu null. */
export function kianUsageFrom(value: unknown): KianUsage | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<KianUsage>;
  if (!Array.isArray(v.models) || typeof v.calls !== 'number') return null;
  return {
    models: v.models.filter((m): m is string => typeof m === 'string'),
    calls: v.calls,
    tokensIn: typeof v.tokensIn === 'number' ? v.tokensIn : 0,
    tokensOut: typeof v.tokensOut === 'number' ? v.tokensOut : 0,
    costEur: typeof v.costEur === 'number' ? v.costEur : null,
    costEstimated: v.costEstimated === true,
  };
}

/** Ältere Einzelangaben (Briefings: `{ model, tokensIn, tokensOut, costEur }`) in dieselbe Form bringen. */
export function kianUsageFromCall(
  call: { model: string | null; tokensIn: number | null; tokensOut: number | null; costEur: number | null } | null | undefined,
): KianUsage | null {
  if (!call) return null;
  return {
    models: call.model ? [call.model] : [],
    calls: 1,
    tokensIn: call.tokensIn ?? 0,
    tokensOut: call.tokensOut ?? 0,
    costEur: call.costEur,
    costEstimated: false,
  };
}

export function KianUsageLine({ usage, ...textProps }: { usage: KianUsage | null | undefined } & TextProps) {
  if (!usage) return null;
  const details = [
    `Modell: ${usage.models.join(', ') || 'unbekannt'}`,
    `Tokens: ${nf.format(usage.tokensIn)} rein / ${nf.format(usage.tokensOut)} raus`,
    usage.costEur != null
      ? `Kosten: ${formatKianCost(usage.costEur)}${usage.costEstimated ? ' (Pauschale, kein Modellpreis hinterlegt)' : ''}`
      : 'Kosten: unbekannt',
    ...(usage.calls > 1 ? [`${usage.calls} KIAN-Aufrufe zusammengezählt`] : []),
  ];
  return (
    <Tooltip label={details.join('\n')} withArrow multiline w={300} style={{ whiteSpace: 'pre-line' }}>
      <Text size="xs" c="dimmed" {...textProps}>
        {formatKianUsage(usage)}
      </Text>
    </Tooltip>
  );
}

/** Meldungstext plus Verbrauchszeile — für KIAN-Ergebnisse, die nur als Hinweis erscheinen. */
export function kianNotice(message: ReactNode, usage: KianUsage | null | undefined): ReactNode {
  if (!usage) return message;
  return (
    <>
      {message}
      <Text size="xs" c="dimmed" mt={2}>
        {formatKianUsage(usage)}
      </Text>
    </>
  );
}
